<template>
  <div class="taglist-container" :class="{ 'dashboard-mode': dashboardMode }">
    <!-- SDAA 회사인 경우 TagListSdaa 컴포넌트 표시 -->
    <TagListSdaa 
      v-if="$tenant.layout === 'sdaa'"
      :dashboardMode="dashboardMode"
      :filterQuery="$route.query.filter"
    />
    
    <!-- SDAA가 아닌 경우 기존 TagList 표시 -->
    <div v-else>
      <!-- 검색 섹션 -->
      <div class="search-section">
        <div class="search-group">
          <div class="filter-buttons" v-if="$tenant.layout === 'sdaa'">
            <!-- 첫 번째 줄: 733버튼 -->
            <div class="filter-row">
              <button 
                @click="toggleCodeGroup('73_')" 
                class="filter-btn"
                :class="{ active: selectedFilters.codeGroup === '73_' }"
              >
                73
              </button>
              <button 
                @click="toggleCodeGroup('74_')" 
                class="filter-btn"
                :class="{ active: selectedFilters.codeGroup === '74_' }"
              >
                74
              </button>
              <button 
                @click="toggleCodeGroup('83_')" 
                class="filter-btn"
                :class="{ active: selectedFilters.codeGroup === '83_' }"
              >
                83
              </button>
              <button @click="clearCodeGroup" class="filter-btn clear-btn" :class="{ active: !selectedFilters.codeGroup }">All</button>
            </div>
            <!-- 두 번째 줄: 나머지 버튼들 -->
            <div class="filter-row">
              <button 
                @click="toggleDeviceGroup('H_Pulse')" 
                class="filter-btn"
                :class="{ active: selectedFilters.deviceGroup === 'H_Pulse' }"
              >
                H_Pulse
              </button>
              <button 
                @click="toggleDeviceGroup('DVT')" 
                class="filter-btn"
                :class="{ active: selectedFilters.deviceGroup === 'DVT' }"
              >
                DVT
              </button>
              <button 
                @click="toggleDeviceGroup('Nebulizer')" 
                class="filter-btn"
                :class="{ active: selectedFilters.deviceGroup === 'Nebulizer' }"
              >
                Nebulizer
              </button>
              <button 
                @click="toggleDeviceGroup('SCD')" 
                class="filter-btn"
                :class="{ active: selectedFilters.deviceGroup === 'SCD' }"
              >
                SCD
              </button>
              <button 
                @click="toggleDeviceGroup('Pulse_Oxi')" 
                class="filter-btn"
                :class="{ active: selectedFilters.deviceGroup === 'Pulse_Oxi' }"
              >
                Pulse_Oxi
              </button>
              <button 
                @click="toggleDeviceGroup('H_Pulse_Oxi')" 
                class="filter-btn"
                :class="{ active: selectedFilters.deviceGroup === 'H_Pulse_Oxi' }"
              >
                H_Pulse_Oxi
              </button>
              <button 
                @click="toggleDeviceGroup('Infusion')" 
                class="filter-btn"
                :class="{ active: selectedFilters.deviceGroup === 'Infusion' }"
              >
                Infusion
              </button>
              <button 
                @click="toggleDeviceGroup('Feeding')" 
                class="filter-btn"
                :class="{ active: selectedFilters.deviceGroup === 'Feeding' }"
              >
                Feeding
              </button>
              <button 
                @click="toggleDeviceGroup('Patient_M')" 
                class="filter-btn"
                :class="{ active: selectedFilters.deviceGroup === 'Patient_M' }"
              >
                Patient_M
              </button>
              <button @click="clearDeviceGroup" class="filter-btn clear-btn" :class="{ active: !selectedFilters.deviceGroup }">All</button>
            </div>
          </div>
        </div>

        <!-- 메인 콘텐츠 카드 -->
        <div class="content-card">
          <div class="card-header">
            <h2>Tag List</h2>
            <div class="header-controls">
              <!-- 헤더 우측: 검색 + per page -->
              <div class="header-search">
                <input 
                  type="text" 
                  v-model="selectedFilters.searchText" 
                  class="search-input"
                  placeholder="Search all fields (minimum 2 characters)"
                  @input="handleSearchInput"
                >
                <div v-if="!dashboardMode" class="items-per-page-selector">
                  <label for="itemsPerPage">Items per page:</label>
                  <select 
                    id="itemsPerPage" 
                    v-model="itemsPerPage" 
                    @change="changeItemsPerPage"
                    class="items-select"
                  >
                    <option :value="10">10</option>
                    <option :value="20">20</option>
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                  </select>
                </div>
              </div>
              <div class="view-toggle" v-if="isMobile">
                <button 
                  @click="toggleView" 
                  class="view-toggle-btn"
                  :class="{ active: showCardView }"
                >
                  {{ showCardView ? 'Table View' : 'Card View' }}
                </button>
              </div>
              <div class="results-info" v-if="totalItems > 0">
                <template v-if="dashboardMode">최신 10개 (전체 {{ totalItems }}개)</template>
                <template v-else>Showing {{ ((currentPage - 1) * itemsPerPage) + 1 }}-{{ Math.min(currentPage * itemsPerPage, totalItems) }} of {{ totalItems }} results</template>
              </div>
            </div>
          </div>
          
          <!-- 카드뷰 (모바일 세로모드용) -->
          <div v-if="showCardView && isMobile" class="card-view-container">
            <div v-for="tag in tags" :key="tag.tag_mac" class="tag-card" :class="{ 'card-problem': isTagProblematic(tag.tag_mac) }">
              <div class="tag-card-header">
                <div class="tag-mac">{{ tag.tag_mac }}</div>
                <div class="device-name">{{ tag.devicename || 'N/A' }}</div>
              </div>
              
              <div class="tag-card-body">
                <div class="tag-info-row">
                  <div class="info-item">
                    <span class="info-label">Sensing Time:</span>
                    <span class="info-value">{{ formatSensingTime(tag.tag_sensingtime) }}</span>
                  </div>
                </div>
                
                <div class="tag-info-row">
                  <div class="info-item">
                    <span class="info-label">RSSI:</span>
                    <span class="info-value">
                      <span v-if="Math.abs(tag.tag_rssi) <= 70">
                        <span class="material-symbols-outlined rssi-good" style="font-size: 16px;">signal_cellular_alt</span>
                        <span>({{ tag.tag_rssi }})</span>
                      </span>
                      <span v-else>
                        <span class="material-symbols-outlined rssi-mid" style="font-size: 16px;">signal_cellular_alt_2_bar</span>
                        <span>({{ tag.tag_rssi }})</span>
                      </span>
                    </span>
                  </div>
                  
                  <div class="info-item">
                    <span class="info-label">Temperature:</span>
                    <span class="info-value">
                      <span class="material-symbols-outlined thermostat" style="font-size: 14px;">device_thermostat</span>
                      <span>{{ tag.tag_temperature }} °C</span>
                    </span>
                  </div>
                </div>
                
                <div class="tag-info-row">
                  <div class="info-item">
                    <span class="info-label">Battery:</span>
                    <span class="info-value">
                      <span :class="getBatteryStatus(tag.tag_vol)">
                        {{ tag.tag_vol >= 2.7 ? 'Good' : tag.tag_vol >= 2.3 ? 'Mid' : 'None' }}
                      </span>
                      <span> ({{ tag.tag_vol.toFixed(1) }}v)</span>
                    </span>
                  </div>
                  
                  <div class="info-item">
                    <span class="info-label">Active:</span>
                    <span class="info-value" :class="{ 'active-yes': tag.active === 0, 'active-no': tag.active !== 0 }">
                      {{ tag.active === 0 ? 'Yes' : 'No' }}
                    </span>
                  </div>
                </div>
                
                <div class="tag-info-row">
                  <div class="info-item full-width">
                    <span class="info-label">Gateway MAC:</span>
                    <span class="info-value">{{ tag.gw_mac_parent }}</span>
                  </div>
                </div>
                
                <div class="tag-info-row">
                  <div class="info-item full-width">
                    <span class="info-label">Location:</span>
                    <span class="info-value">{{ tag.taglocation || 'N/A' }}</span>
                  </div>
                </div>
              </div>
              
              <div v-if="!dashboardMode" class="tag-card-footer">
                <button @click="openEditSidebar(tag)" class="edit-btn card-edit-btn">EDIT</button>
              </div>
            </div>
          </div>
          
          <!-- 테이블 컨테이너 -->
          <div v-else class="table-container">
            <table class="tags-table">
              <thead>
                <tr>
                  <th @click="sortTags('tag_mac')">Tag MAC <span :class="getArrowClass('tag_mac')"></span></th>
                  <th @click="sortTags('devicename')">Device Name <span :class="getArrowClass('devicename')"></span></th>
                  <th @click="sortTags('tag_sensingtime')">Sensing Time <span :class="getArrowClass('tag_sensingtime')"></span></th>
                  <th @click="sortTags('tag_rssi')">RSSI <span :class="getArrowClass('tag_rssi')"></span></th>
                  <th @click="sortTags('tag_temperature')">Temperature <span :class="getArrowClass('tag_temperature')"></span></th>
                  <th @click="sortTags('tag_vol')">Battery Status <span :class="getArrowClass('tag_vol')"></span></th>
                  <th @click="sortTags('gw_mac_parent')">Gateway MAC <span :class="getArrowClass('gw_mac_parent')"></span></th>
                  <th @click="sortTags('taglocation')">Location<span :class="getArrowClass('taglocation')"></span></th>
                  <th @click="sortTags('active')">Active <span :class="getArrowClass('active')"></span></th>
                  <th v-if="!dashboardMode">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="tag in tags" :key="tag.tag_mac" :class="{ 'row-problem': isTagProblematic(tag.tag_mac) }">
                  <td>{{ tag.tag_mac }}</td>
                  <td>{{ tag.devicename }}</td>
                  <td>{{ formatSensingTime(tag.tag_sensingtime) }}</td>
                  <td>
                    <span v-if="Math.abs(tag.tag_rssi) <= 70">
                      <span class="material-symbols-outlined rssi-good" style="font-size: 24px;">signal_cellular_alt</span>
                      <span>({{ tag.tag_rssi }})</span>
                    </span>
                    <span v-else>
                      <span class="material-symbols-outlined rssi-mid" style="font-size: 24px;">signal_cellular_alt_2_bar</span>
                      <span>({{ tag.tag_rssi }})</span>
                    </span>
                  </td>
                  <td>
                    <span class="material-symbols-outlined thermostat" style="font-size: 16px;">device_thermostat</span>
                    <span> {{ tag.tag_temperature }} °C </span>
                  </td>
                  <td>
                    <span :class="getBatteryStatus(tag.tag_vol)">
                      {{ tag.tag_vol >= 2.7 ? 'Good' : tag.tag_vol >= 2.3 ? 'Mid' : 'None' }}
                    </span>
                    <span> ({{ tag.tag_vol.toFixed(1) }}v)</span>
                  </td>
                  <td>{{ tag.gw_mac_parent }}</td>
                  <td>{{ tag.taglocation }}</td>
                  <td>{{ tag.active === 0 ? 'Yes' : 'No' }}</td>
                  <td v-if="!dashboardMode">
                    <button @click="openEditSidebar(tag)" class="edit-btn">EDIT</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- 페이지네이션 (대시보드에서는 숨김, 더보기로 전체 페이지 이동) -->
          <div v-if="!dashboardMode" class="pagination">
            <button @click="prevPage" :disabled="currentPage === 1" class="pagination-btn">Previous</button>
            <span class="page-numbers">
              <span v-for="page in visiblePages" :key="page" @click="goToPage(page)" :class="{ active: currentPage === page }" class="page-number">{{ page }}</span>
            </span>
            <button @click="nextPage" :disabled="currentPage === totalPages" class="pagination-btn">Next</button>
          </div>
        </div>

        <!-- Edit Tag Sidebar -->
        <EditTag
          v-if="selectedTag"
          :visible="isEditSidebarVisible"
          :tag="selectedTag"
          @save="updateTag"
          @delete="deleteTag"
          @close="closeEditSidebar"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import EditTag from './EditTag.vue';
import TagListSdaa from './TagListSdaa.vue';

export default {
  name: 'TagList',
  components: {
    EditTag,
    TagListSdaa,
  },
  props: {
    dashboardMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tags: [],
      // 기본 정렬: sensing time 최신순 (DESC = -1)
      sortKey: 'tag_sensingtime',
      sortOrder: -1,
      updateInterval: 10,
      updateTimer: null,
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      company: localStorage.getItem('company') || '',
      selectedFilters: {
        searchText: '',
        battery: '',
        rssi: '',
        active: '',
        location: '',
        startDate: '',
        endDate: '',
        codeGroup: '', // 73,74,83 중 하나
        deviceGroup: '' // 나머지 중 하나
      },
      filters: {
        searchText: '',
        battery: '',
        rssi: '',
        active: '',
        location: '',
        startDate: '',
        endDate: ''
      },
      filterQuery: '', // 추가: 쿼리 파라미터용
      searchInfo: '',
      searchTimeout: null,
      isEditSidebarVisible: false,
      selectedTag: null,
      showCardView: false,
      isMobile: false,
      problemTagMacs: new Set(),
    };
  },
  created() {
    // company 정보 설정
    this.company = localStorage.getItem('company') || '';
    
    // 대시보드 모드에서는 적은 데이터로 시작
    if (this.dashboardMode) {
      this.itemsPerPage = 10;  // 5개에서 10개로 변경
    }
    // 쿼리 파라미터에서 filter 추출
    this.filterQuery = this.$route.query.filter || '';
    this.checkIfMobile();
    this.loadTags(); // 데이터 로드 메소드 호출
    // 대시보드 모드에서는 자동 갱신을 늦게 시작
    if (this.dashboardMode) {
      // 대시보드 모드에서는 30초 간격으로 갱신
      setTimeout(() => {
        this.updateInterval = 30;
        this.startUpdateTimer();
      }, 5000); // 5초 후에 자동 갱신 시작
    } else {
      this.startUpdateTimer();
    }
  },
  watch: {
    'selectedFilters.codeGroup'() {
      this.updateSearchText();
    },
    'selectedFilters.deviceGroup'() {
      this.updateSearchText();
    }
  },
  mounted() {
    window.addEventListener('resize', this.checkIfMobile);
  },
  beforeUnmount() {
    if (this.updateTimer) {
      clearInterval(this.updateTimer);
    }
    window.removeEventListener('resize', this.checkIfMobile);
  },
  methods: {
    updateSearchText() {
      const arr = [];
      if (this.selectedFilters.codeGroup) arr.push(this.selectedFilters.codeGroup);
      if (this.selectedFilters.deviceGroup) arr.push(this.selectedFilters.deviceGroup);
      this.filters.searchText = arr.join('');
      this.currentPage = 1;
      this.loadTags();
    },
    checkIfMobile() {
      this.isMobile = window.innerWidth <= 768;
      // 데스크톱으로 변경되면 테이블 뷰로 자동 전환
      if (!this.isMobile) {
        this.showCardView = false;
      }
    },
    toggleView() {
      this.showCardView = !this.showCardView;
    },
    loadTags() {
      const company = localStorage.getItem('company');
      // 대시보드 모드: 최신 수신순(tag_sensingtime) 10개만, 페이지네이션 없음
      const limit = this.dashboardMode ? 10 : this.itemsPerPage;
      const offset = this.dashboardMode ? 0 : (this.currentPage - 1) * this.itemsPerPage;
      const sortKey = this.dashboardMode ? 'tag_sensingtime' : this.sortKey;
      const sortOrder = this.dashboardMode ? -1 : this.sortOrder;

      const params = {
        company,
        limit,
        offset,
        sortKey,
        sortOrder
      };

      // 검색 필터 추가
      if (this.filters.searchText && this.filters.searchText.length >= 2) {
        params.searchText = this.filters.searchText;
        console.log('검색어:', this.filters.searchText); // 디버깅용
      }
      // filter 쿼리 파라미터가 있으면 devicename 필터로 추가
      if (this.filterQuery) {
        params.devicename = this.filterQuery;
      }
      
      console.log('API 요청 파라미터:', params); // 디버깅용
      
      axios.get('/api/tagList', { params })
      .then(response => {
        console.log('Response:', response.data);
        this.tags = response.data.data || [];
        this.totalItems = response.data.total || 0;
        this.updateSearchInfo();
      })
      .catch(error => {
        console.error('Error fetching tag data:', error);
        this.tags = [];
        this.totalItems = 0;
      });

      // 문제 있는 태그 목록도 로드
      this.loadProblemTags();
    },
    
    sortTags(key) {
      this.sortOrder = this.sortKey === key ? -this.sortOrder : 1;
      this.sortKey = key;
      this.currentPage = 1;
      this.loadTags(); // 서버에서 새로 데이터 로드
    },
    
    changeItemsPerPage() {
      this.currentPage = 1; // 페이지를 첫 번째로 리셋
      this.loadTags(); // 새로운 아이템 수로 데이터 로드
    },
    
    // 페이지네이션 메서드들
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.loadTags();
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.loadTags();
      }
    },
    
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.loadTags();
      }
    },
    
    updateSearchInfo() {
      if (this.filters.searchText && this.filters.searchText.length >= 2) {
        this.searchInfo = `Found ${this.totalItems} result(s) for "${this.filters.searchText}"`;
      } else {
        this.searchInfo = '';
      }
    },
    
    handleSearchInput() {
      clearTimeout(this.searchTimeout);
      
      if (this.selectedFilters.searchText.length > 0 && this.selectedFilters.searchText.length < 2) {
        this.searchInfo = '최소 2자 이상 입력하세요';
        return;
      }
      
      this.searchInfo = '';
      
      if (this.selectedFilters.searchText.length >= 2) {
        this.searchTimeout = setTimeout(() => {
          this.filters.searchText = this.selectedFilters.searchText;
          this.currentPage = 1;
          this.loadTags();
        }, 500);
      } else if (this.selectedFilters.searchText.length === 0) {
        this.filters.searchText = '';
        this.currentPage = 1;
        this.loadTags();
      }
    },
    
    startUpdateTimer() {
      if (this.updateTimer) {
        clearInterval(this.updateTimer);
      }
      this.updateTimer = setInterval(() => {
        this.loadTags();
      }, this.updateInterval * 1000);
    },
    formatSensingTime(sensingTime) {
      if (!sensingTime) return 'N/A';
      const date = new Date(sensingTime);
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const years = date.getFullYear();
      return `${years}/${month}/${day} ${hours}:${minutes}`;
    },
    getBatteryStatus(voltage) {
      if (voltage >= 2.7) {
        return 'battery_good';
      } else if (voltage >= 2.3 && voltage < 2.7) {
        return 'battery_mid';
      } else {
        return 'battery_none';
      }
    },
    getArrowClass(key) {
      if (this.sortKey === key) {
        return this.sortOrder === 1 ? 'arrow-up' : 'arrow-down';
      }
      return 'arrow-default';
    },
    loadProblemTags() {
      const company = localStorage.getItem('company');
      axios.get('/api/problemTagMacs', { params: { company } })
        .then(response => {
          const allMacs = [
            ...(response.data.warning || []),
            ...(response.data.notification || [])
          ];
          this.problemTagMacs = new Set(allMacs);
        })
        .catch(() => {
          this.problemTagMacs = new Set();
        });
    },
    isTagProblematic(tagMac) {
      return this.problemTagMacs.has(tagMac);
    },
    async deleteTag(tagMac) {
      try {
        const company = localStorage.getItem('company');
        const response = await axios.delete('/api/deleteTag', {
          data: { tag_mac: tagMac, company }
        });
        if (response.data.success) {
          alert('태그가 성공적으로 삭제되었습니다.');
          this.closeEditSidebar();
          this.loadTags();
        } else {
          alert('태그 삭제에 실패했습니다.');
        }
      } catch (error) {
        console.error('Tag delete error:', error);
        alert('태그 삭제 중 오류가 발생했습니다: ' + error.message);
      }
    },
    openEditSidebar(tag) {
      this.selectedTag = tag;
      this.isEditSidebarVisible = true;
    },
    async updateTag(updatedTag) {
      try {
        console.log('=== TAG UPDATE DEBUG START ===');
        console.log('Original updatedTag:', updatedTag);
        
        // company 정보 추가 확인
        const company = localStorage.getItem('company');
        console.log('Company from localStorage:', company);
        
        // FormData 객체 생성 (AssetPage.vue와 동일한 방식)
        const formData = new FormData();
        
        // 필드들을 FormData에 추가
        formData.append('tag_mac', updatedTag.tag_mac);
        formData.append('devicename', updatedTag.devicename || '');
        formData.append('taglocation', updatedTag.taglocation || '');
        formData.append('building', updatedTag.building || '');
        formData.append('floor', updatedTag.floor || '');
        formData.append('assignedzone', updatedTag.assignedzone || '');
        formData.append('devicetype', updatedTag.devicetype || '');
        formData.append('company', company);
        
        // 디버깅을 위한 FormData 내용 출력
        console.log('Sending FormData:');
        for (let [key, value] of formData.entries()) {
          console.log(`${key}: ${value}`);
        }
        
        console.log('Calling API: /api/updateAssetDetails');
        
        // AssetPage.vue와 동일한 API 엔드포인트 사용
        const response = await axios.post('/api/updateAssetDetails', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        console.log('Raw response:', response);
        console.log('Response status:', response.status);
        console.log('Response data:', response.data);
        
        if (response.status === 200 || response.status === 204) {
          console.log('태그 업데이트 성공:', response.data);
          
          // 로컬 데이터 업데이트
          const index = this.tags.findIndex(tag => tag.tag_mac === updatedTag.tag_mac);
          if (index !== -1) {
            this.tags[index] = { ...this.tags[index], ...updatedTag };
          }
          
          // 목록 새로고침
          await this.loadTags();
          
          // 편집 모드 종료
          this.selectedTag = null;
          this.isEditSidebarVisible = false;
          
          alert('태그가 성공적으로 업데이트되었습니다.');
        } else {
          console.error('Unexpected response status:', response.status);
          alert('태그 업데이트에 실패했습니다.');
        }
        
        console.log('=== TAG UPDATE DEBUG END ===');
        
      } catch (error) {
        console.error('=== TAG UPDATE ERROR ===');
        console.error('Error object:', error);
        console.error('Error message:', error.message);
        
        if (error.response) {
          console.error('Error response status:', error.response.status);
          console.error('Error response data:', error.response.data);
          console.error('Error response headers:', error.response.headers);
        }
        
        alert('태그 업데이트 중 오류가 발생했습니다: ' + error.message);
      }
    },
    closeEditSidebar() {
      this.selectedTag = null;
      this.isEditSidebarVisible = false;
    },
    applyFilter(filterValue) {
      console.log('applyFilter 호출됨:', filterValue);
      this.filters.searchText = filterValue;
      this.selectedFilters.searchText = filterValue;
      this.currentPage = 1;
      console.log('searchText 설정됨:', this.filters.searchText);
      this.loadTags();
    },
    clearFilter() {
      console.log('clearFilter 호출됨');
      this.selectedFilters.codeGroup = '';
      this.selectedFilters.deviceGroup = '';
      this.filters.searchText = '';
      this.selectedFilters.searchText = '';
      this.currentPage = 1;
      console.log('searchText 초기화됨:', this.filters.searchText);
      this.loadTags();
    },
    toggleCodeGroup(value) {
      if (this.selectedFilters.codeGroup === value) {
        this.selectedFilters.codeGroup = '';
      } else {
        this.selectedFilters.codeGroup = value;
      }
      this.updateSearchText();
    },
    toggleDeviceGroup(value) {
      if (this.selectedFilters.deviceGroup === value) {
        this.selectedFilters.deviceGroup = '';
      } else {
        this.selectedFilters.deviceGroup = value;
      }
      this.updateSearchText();
    },
    clearCodeGroup() {
      this.selectedFilters.codeGroup = '';
    },
    clearDeviceGroup() {
      this.selectedFilters.deviceGroup = '';
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      // 끝에서 시작점 조정
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
};
</script>

<style scoped>
.taglist-container {
  padding: 12px 16px;
  max-width: 1680px;
  margin: 0 auto;
  background-color: #f5f5f5;
}

/* 대시보드 카드 안에 들어갈 때 여백/배경 축소 */
.taglist-container.dashboard-mode {
  padding: 0;
  max-width: 100%;
  background-color: transparent;
}

.taglist-container.dashboard-mode .content-card {
  border-radius: 0;
  box-shadow: none;
  border: none;
  padding: 0;
}

.taglist-container.dashboard-mode .card-header {
  padding: 12px 16px;
}

.taglist-container.dashboard-mode .table-container {
  padding: 0;
}

/* 검색 섹션 — 카드 헤더 내부에 검색이 들어있어 외부 섹션은 거의 0 */
.search-section {
  margin-bottom: 0;
  display: flex;
  justify-content: center;
  padding: 0;
}

.search-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
}

.search-input {
  width: 320px;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 13px;
  background: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: #4e73df;
  box-shadow: 0 4px 12px rgba(78, 115, 223, 0.15);
}

.items-per-page-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.items-per-page-selector label {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.items-select {
  padding: 6px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 70px;
}

.items-select:focus {
  outline: none;
  border-color: #4e73df;
  box-shadow: 0 2px 8px rgba(78, 115, 223, 0.15);
}

.search-info {
  font-size: 12px;
  color: #666;
  text-align: center;
  font-style: italic;
  width: 100%;
  margin-top: 8px;
}

/* 메인 콘텐츠 카드 */
.content-card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.card-header {
  padding: 12px 18px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.card-header h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  letter-spacing: 0.2px;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.view-toggle-btn {
  background: #4e73df;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
}

.view-toggle-btn:hover {
  background: #3a5bc7;
}

.view-toggle-btn.active {
  background: #1cc88a;
}

.results-info {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

/* 테이블 컨테이너 */
.table-container {
  overflow-x: auto;
  max-height: 70vh;
  overflow-y: auto;
}

.tags-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.tags-table thead {
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
}

.tags-table thead th {
  background-color: #f8fafc;
  padding: 9px 12px;
  text-align: left;
  cursor: pointer;
  position: relative;
  font-weight: 600;
  font-size: 12px;
  color: #4b5563;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
  user-select: none;
  transition: background-color 0.15s;
  letter-spacing: 0.1px;
}

.tags-table thead th:hover {
  background-color: #eef2f7;
}

.tags-table tbody td {
  padding: 8px 12px;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
  color: #1f2937;
}

.tags-table tbody tr:hover {
  background-color: #f9fafb;
}

/* 정렬 화살표 */
.arrow-default::after {
  content: '↕';
  color: #ccc;
  margin-left: 5px;
}

.arrow-up::after {
  content: '↑';
  color: #4e73df;
  margin-left: 5px;
}

.arrow-down::after {
  content: '↓';
  color: #4e73df;
  margin-left: 5px;
}

/* 아이콘 및 상태 스타일 */
.rssi-good {
  color: #1cc88a;
}

.rssi-mid {
  color: #f6c23e;
}

.rssi-low {
  color: #e74a3b;
}

.thermostat {
  color: #e74a3b;
}

.battery_good {
  color: #1cc88a;
}

.battery_mid {
  color: #f6c23e;
}

.battery_none {
  color: #36b9cc;
}

/* 페이지네이션 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  gap: 8px;
  flex-wrap: wrap;
  border-top: 1px solid #e0e0e0;
  background: #f8f9fa;
}

/* Edit 버튼 스타일 */
.edit-btn {
  padding: 6px 12px;
  border: 1px solid #e0e0e0;
  background-color: #4e73df;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
}

.edit-btn:hover {
  background-color: #3a5bc7;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(78, 115, 223, 0.3);
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid #e0e0e0;
  background-color: white;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 500;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #4e73df;
  color: white;
  border-color: #4e73df;
}

.pagination-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
  background-color: #f8f9fa;
}

.page-numbers {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.page-number {
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  background-color: white;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  font-size: 14px;
  min-width: 40px;
  text-align: center;
}

.page-number:hover {
  background-color: #4e73df;
  color: white;
  border-color: #4e73df;
}

.page-number.active {
  background-color: #4e73df;
  color: white;
  border-color: #4e73df;
  font-weight: 600;
}

/* 반응형 디자인 */
@media screen and (max-width: 1200px) {
  .taglist-container {
    padding: 8px;
    max-width: 100%;
  }
  
  .search-input {
    width: 350px;
  }
  
  .card-header {
    padding: 16px 20px;
  }
  
  .card-header h2 {
    font-size: 22px;
  }
  
  .results-info {
    font-size: 13px;
  }
  
  .tags-table {
    font-size: 13px;
  }
  
  .tags-table thead th {
    padding: 12px 8px;
  }
  
  .tags-table tbody td {
    padding: 10px 8px;
  }
}

/* 필터 버튼 스타일 */
.filter-buttons {
  display: flex;
    flex-direction: column;
    gap: 6px;
  margin-top: 8px;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 100%;
  }
  
.filter-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  }
  
.filter-btn {
  padding: 6px 12px;
  border: 1px solid #e0e0e0;
  background-color: #f8f9fa;
  color: #333;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
  min-width: 80px;
  text-align: center;
  margin: 2px;
}

.filter-btn:hover {
  background-color: #e9ecef;
}

.filter-btn.active {
  background-color: #4e73df;
  color: white;
  border-color: #4e73df;
}

/* 문제 태그 하이라이트 - 테이블 행 */
.row-problem td {
  background-color: #fff3e0 !important;
  border-left: 3px solid #e65100;
}

.row-problem:hover td {
  background-color: #ffe0b2 !important;
}

/* 문제 태그 하이라이트 - 카드뷰 */
.card-problem {
  border-left: 4px solid #e65100 !important;
  background-color: #fff8e1 !important;
  box-shadow: 0 2px 8px rgba(230, 81, 0, 0.15) !important;
}

.clear-btn {
  background-color: #dc3545;
  color: white;
}

.clear-btn:hover {
  background-color: #c82333;
}
</style>